import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchUnits, createUnit, updateUnit, assignUnitType, bulkAssignUnitType, bulkUpdateUnitStatus, deleteUnit } from '../api/unitsApi'
import { fetchUnitTypes } from '../api/unitTypesApi'

function UnitForm({ unit, unitTypes, onSave, onCancel, isPending }) {
  const [formData, setFormData] = useState({
    unit_number: unit?.unit_number || '',
    unit_type_id: unit?.unit_type_id || '',
    bedrooms: unit?.bedrooms || '',
    bathrooms: unit?.bathrooms || '',
    sqm: unit?.sqm || '',
    price: unit?.price || '',
    rent_per_week: unit?.rent_per_week || '',
    status: unit?.status || 'draft',
  })

  const handleUnitTypeChange = (unitTypeId) => {
    setFormData({ ...formData, unit_type_id: unitTypeId })

    if (unitTypeId) {
      const selectedType = unitTypes.find(t => t.id === unitTypeId)
      if (selectedType) {
        setFormData({
          ...formData,
          unit_type_id: unitTypeId,
          bedrooms: selectedType.bedrooms || formData.bedrooms,
          bathrooms: selectedType.bathrooms || formData.bathrooms,
          sqm: selectedType.internal_size_sqm || formData.sqm,
          price: selectedType.default_sale_price || formData.price,
          rent_per_week: selectedType.rent_per_week || formData.rent_per_week,
        })
      }
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave({
      ...formData,
      bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
      bathrooms: formData.bathrooms ? parseFloat(formData.bathrooms) : null,
      sqm: formData.sqm ? parseFloat(formData.sqm) : null,
      price: formData.price ? parseFloat(formData.price) : null,
      rent_per_week: formData.rent_per_week ? parseFloat(formData.rent_per_week) : null,
      unit_type_id: formData.unit_type_id || null,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="label">Unit Number *</label>
          <input
            type="text"
            value={formData.unit_number}
            onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
            className="input"
            required
            placeholder="e.g., 101, A1, PH1"
          />
        </div>

        <div>
          <label className="label">Unit Type</label>
          <select
            value={formData.unit_type_id}
            onChange={(e) => handleUnitTypeChange(e.target.value)}
            className="input"
          >
            <option value="">Select Type (optional)</option>
            {unitTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.label} ({type.bedrooms}bd/{type.bathrooms}ba - {type.internal_size_sqm || 0}sqm)
              </option>
            ))}
          </select>
          <p className="text-xs text-secondary-600 mt-1">
            Selecting a type will auto-fill values below
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className="label">Bedrooms</label>
          <input
            type="number"
            value={formData.bedrooms}
            onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
            className="input"
            min="0"
            placeholder="0"
          />
        </div>

        <div>
          <label className="label">Bathrooms</label>
          <input
            type="number"
            step="0.5"
            value={formData.bathrooms}
            onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
            className="input"
            min="0"
            placeholder="0"
          />
        </div>

        <div>
          <label className="label">Floor Area (sqm)</label>
          <input
            type="number"
            step="0.1"
            value={formData.sqm}
            onChange={(e) => setFormData({ ...formData, sqm: e.target.value })}
            className="input"
            placeholder="0"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className="label">Price ($)</label>
          <input
            type="number"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
            className="input"
            placeholder="0"
          />
        </div>

        <div>
          <label className="label">Rent per Week ($)</label>
          <input
            type="number"
            value={formData.rent_per_week}
            onChange={(e) => setFormData({ ...formData, rent_per_week: e.target.value })}
            className="input"
            placeholder="e.g., 550"
          />
        </div>

        <div>
          <label className="label">Status</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="input"
          >
            <option value="draft">Draft</option>
            <option value="available">Available</option>
            <option value="reserved">Reserved</option>
            <option value="sold">Sold</option>
          </select>
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <button type="button" onClick={onCancel} className="btn-outline flex-1 py-2">
          Cancel
        </button>
        <button
          type="submit"
          disabled={isPending}
          className="btn-primary flex-1 py-2"
        >
          {isPending ? 'Saving...' : unit ? 'Update Unit' : 'Add Unit'}
        </button>
      </div>
    </form>
  )
}

export default function UnitsManager({ projectId }) {
  const queryClient = useQueryClient()
  const [selectedUnits, setSelectedUnits] = useState(new Set())
  const [editingUnit, setEditingUnit] = useState(null)
  const [showAddUnit, setShowAddUnit] = useState(false)
  const [filterStatus, setFilterStatus] = useState('all')
  const [error, setError] = useState(null)

  const { data: unitsData, isLoading: unitsLoading } = useQuery({
    queryKey: ['units', projectId],
    queryFn: () => fetchUnits(projectId),
  })

  const { data: unitTypesData } = useQuery({
    queryKey: ['unit-types', projectId],
    queryFn: () => fetchUnitTypes(projectId),
  })

  const units = unitsData?.data || []
  const unitTypes = unitTypesData?.data || []

  const filteredUnits = filterStatus === 'all'
    ? units
    : units.filter(u => u.status === filterStatus)

  const assignTypeMutation = useMutation({
    mutationFn: ({ unitId, unitTypeId }) => assignUnitType(unitId, unitTypeId, true),
    onSuccess: () => {
      queryClient.invalidateQueries(['units', projectId])
      setError(null)
    },
    onError: (err) => {
      setError(err.message || 'Failed to assign unit type')
    },
  })

  const bulkAssignMutation = useMutation({
    mutationFn: ({ unitIds, unitTypeId }) => bulkAssignUnitType(unitIds, unitTypeId, true),
    onSuccess: () => {
      queryClient.invalidateQueries(['units', projectId])
      setSelectedUnits(new Set())
      setError(null)
    },
    onError: (err) => {
      setError(err.message || 'Failed to bulk assign unit types')
    },
  })

  const bulkStatusMutation = useMutation({
    mutationFn: ({ unitIds, status }) => bulkUpdateUnitStatus(unitIds, status),
    onSuccess: () => {
      queryClient.invalidateQueries(['units', projectId])
      setSelectedUnits(new Set())
      setError(null)
    },
    onError: (err) => {
      setError(err.message || 'Failed to update unit status')
    },
  })

  const createMutation = useMutation({
    mutationFn: (unitData) => createUnit({ ...unitData, project_id: projectId }),
    onSuccess: () => {
      queryClient.invalidateQueries(['units', projectId])
      setShowAddUnit(false)
      setError(null)
    },
    onError: (err) => {
      setError(err.message || 'Failed to create unit')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }) => updateUnit(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries(['units', projectId])
      setEditingUnit(null)
      setError(null)
    },
    onError: (err) => {
      setError(err.message || 'Failed to update unit')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteUnit,
    onSuccess: () => {
      queryClient.invalidateQueries(['units', projectId])
      setError(null)
    },
    onError: (err) => {
      setError(err.message || 'Failed to delete unit')
    },
  })

  const toggleUnitSelection = (unitId) => {
    const newSelection = new Set(selectedUnits)
    if (newSelection.has(unitId)) {
      newSelection.delete(unitId)
    } else {
      newSelection.add(unitId)
    }
    setSelectedUnits(newSelection)
  }

  const selectAll = () => {
    if (selectedUnits.size === filteredUnits.length) {
      setSelectedUnits(new Set())
    } else {
      setSelectedUnits(new Set(filteredUnits.map(u => u.id)))
    }
  }

  const handleBulkAssignType = () => {
    if (selectedUnits.size === 0) {
      alert('Please select units first')
      return
    }

    const unitTypeId = prompt('Enter unit type ID or select from list:')
    if (!unitTypeId) return

    bulkAssignMutation.mutate({
      unitIds: Array.from(selectedUnits),
      unitTypeId,
    })
  }

  const handleBulkChangeStatus = (status) => {
    if (selectedUnits.size === 0) {
      alert('Please select units first')
      return
    }

    if (!confirm(`Change ${selectedUnits.size} units to ${status} status?`)) {
      return
    }

    bulkStatusMutation.mutate({
      unitIds: Array.from(selectedUnits),
      status,
    })
  }

  const handleQuickAssignType = (unitId, unitTypeId) => {
    assignTypeMutation.mutate({ unitId, unitTypeId })
  }

  const handleDeleteUnit = (unitId) => {
    if (!confirm('Delete this unit? This action cannot be undone.')) {
      return
    }
    deleteMutation.mutate(unitId)
  }

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'draft':
        return 'bg-blue-100 text-blue-800'
      case 'available':
        return 'bg-green-100 text-green-800'
      case 'reserved':
        return 'bg-amber-100 text-amber-800'
      case 'sold':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-secondary-100 text-secondary-800'
    }
  }

  if (unitsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="spinner spinner-lg text-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h3 className="text-lg font-semibold text-secondary-900">Units</h3>
          <p className="text-sm text-secondary-600">
            {units.length} total units • {selectedUnits.size} selected
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="input py-2 text-sm"
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="available">Available</option>
            <option value="reserved">Reserved</option>
            <option value="sold">Sold</option>
          </select>
          <button
            onClick={() => setShowAddUnit(true)}
            className="btn-primary px-4 py-2"
          >
            Add Unit
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {showAddUnit && (
        <div className="card p-6 bg-secondary-50">
          <h4 className="text-lg font-semibold text-secondary-900 mb-4">Add New Unit</h4>
          <UnitForm
            unitTypes={unitTypes}
            onSave={(data) => createMutation.mutate(data)}
            onCancel={() => setShowAddUnit(false)}
            isPending={createMutation.isPending}
          />
        </div>
      )}

      {selectedUnits.size > 0 && (
        <div className="card p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <span className="text-sm font-medium text-blue-900">
              {selectedUnits.size} units selected
            </span>
            <div className="flex gap-2 flex-wrap">
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    bulkAssignMutation.mutate({
                      unitIds: Array.from(selectedUnits),
                      unitTypeId: e.target.value,
                    })
                    e.target.value = ''
                  }
                }}
                className="input py-1.5 text-sm"
                disabled={bulkAssignMutation.isPending}
              >
                <option value="">Assign Type...</option>
                {unitTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    Type {type.label}
                  </option>
                ))}
              </select>

              <button
                onClick={() => handleBulkChangeStatus('available')}
                className="btn-outline text-sm py-1.5"
                disabled={bulkStatusMutation.isPending}
              >
                Mark Available
              </button>
              <button
                onClick={() => handleBulkChangeStatus('draft')}
                className="btn-outline text-sm py-1.5"
                disabled={bulkStatusMutation.isPending}
              >
                Mark Draft
              </button>
              <button
                onClick={() => setSelectedUnits(new Set())}
                className="btn-outline text-sm py-1.5"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {units.length === 0 ? (
        <div className="card p-8 text-center">
          <div className="text-secondary-400 mb-3">
            <svg
              className="w-12 h-12 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
          </div>
          <h4 className="text-lg font-semibold text-secondary-900 mb-1">No units yet</h4>
          <p className="text-secondary-600">
            Units should have been created when you set up the project
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedUnits.size === filteredUnits.length && filteredUnits.length > 0}
                      onChange={selectAll}
                      className="rounded border-secondary-300"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Unit #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Bed/Bath
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-200">
                {filteredUnits.map((unit) => (
                  <tr key={unit.id} className={selectedUnits.has(unit.id) ? 'bg-blue-50' : ''}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedUnits.has(unit.id)}
                        onChange={() => toggleUnitSelection(unit.id)}
                        className="rounded border-secondary-300"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-secondary-900">
                      {unit.unit_number}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <select
                        value={unit.unit_type_id || ''}
                        onChange={(e) => handleQuickAssignType(unit.id, e.target.value)}
                        className="input py-1 text-xs"
                        disabled={assignTypeMutation.isPending}
                      >
                        <option value="">None</option>
                        {unitTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary-700">
                      {unit.bedrooms || 0}bd / {unit.bathrooms || 0}ba
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary-700">
                      {unit.sqm ? `${unit.sqm} sqm` : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-secondary-900">
                      {unit.price ? `$${unit.price.toLocaleString()}` : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(unit.status)}`}>
                        {unit.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm">
                      <button
                        onClick={() => setEditingUnit(unit)}
                        className="text-primary-600 hover:text-primary-900 mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteUnit(unit.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {units.length > 0 && (
        <div className="card p-6 bg-secondary-50">
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm font-medium text-secondary-600 mb-1">Total Value</p>
              <p className="text-2xl font-bold text-secondary-900">
                ${units.reduce((sum, unit) => sum + (unit.price || 0), 0).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-secondary-600 mb-1">Reserved Units</p>
              <p className="text-2xl font-bold text-amber-600">
                {units.filter(u => u.status === 'reserved').length}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-secondary-600 mb-1">Sold Units</p>
              <p className="text-2xl font-bold text-red-600">
                {units.filter(u => u.status === 'sold').length}
              </p>
            </div>
          </div>
        </div>
      )}

      {editingUnit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="card max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h4 className="text-xl font-semibold text-secondary-900 mb-4">
              Edit Unit {editingUnit.unit_number}
            </h4>
            <UnitForm
              unit={editingUnit}
              unitTypes={unitTypes}
              onSave={(updates) => updateMutation.mutate({ id: editingUnit.id, updates })}
              onCancel={() => setEditingUnit(null)}
              isPending={updateMutation.isPending}
            />
          </div>
        </div>
      )}
    </div>
  )
}
